import { useEffect } from 'react';

/**
 * How long the check-in screen may sit untouched before the candidate's session is ended and the kiosk
 * returns to Welcome.
 *
 * KID-1 owns that this timer EXISTS; `03-capability-kiosk-session-admin` owns what number it counts to. That
 * capability exposes no timeout setting today, so this is the shipped default and the one line to replace
 * when the site-configurable value arrives. Two minutes: long enough for a slow typist entering five fields
 * standing up, short enough that a last-4 SSN is not left on a screen in a branch lobby.
 */
const SHIPPED_IDLE_TIMEOUT_MS = 120_000;

/**
 * `VITE_IDLE_TIMEOUT_MS` raises the timer for testing — **on a local build, or on a NON-PRODUCTION
 * deployment that explicitly sets it.**
 *
 * Walking the flow by hand takes longer than two minutes — reading a screen against a frame, filling
 * eleven fields, checking a queue entry — and the session clearing underneath the person testing it is
 * indistinguishable from a bug.
 *
 * ⚠️ **This used to be gated on `import.meta.env.DEV` alone, and that made it useless where it was
 * actually wanted.** The deployed `dev` environment is built by the same production pipeline as `prod`,
 * so `DEV` is false there, Rollup removed the branch, and setting the variable on that environment did
 * nothing at all. Widening the gate is a deliberate trade, not an oversight.
 *
 * **Production cannot take it, by construction.** The pipeline injects `VITE_ENVIRONMENT` as the app's
 * own vocabulary — `local | npe | production` — and this refuses the override outright when it reads
 * `production`. That check is a compile-time literal like the old one, so the branch is still removed
 * from a production bundle and `scripts/assert-no-dev-doors.mjs` still has nothing to find.
 *
 * **It is opt-in per environment, not on by default.** `dev` and `qa` both build as `npe`, and the
 * pipeline passes this through only where its variable group defines it — so `qa` keeps the shipped 120s
 * by simply not setting it, and is not silently loosened alongside `dev`.
 *
 * What this costs, stated plainly: on any environment that sets it, a candidate's last-4 SSN and date of
 * birth stay on screen for as long as the value says. That is acceptable on a machine only the team
 * touches and is the reason `production` is refused here rather than merely left unset.
 *
 * A missing, empty or unparseable value falls back to the shipped default rather than to no timeout —
 * the failure mode that matters is not "too short".
 */
function idleTimeout(): number {
  if (import.meta.env.VITE_ENVIRONMENT === 'production') return SHIPPED_IDLE_TIMEOUT_MS;
  const override = Number(import.meta.env.VITE_IDLE_TIMEOUT_MS);
  return Number.isFinite(override) && override > 0 ? override : SHIPPED_IDLE_TIMEOUT_MS;
}

export const CHECK_IN_IDLE_TIMEOUT_MS = idleTimeout();

/**
 * Calls `onIdle` once the flow has gone `timeoutMs` without a touch or a key press.
 *
 * Armed ONCE, in `App`, for every screen but Welcome — not per screen. Per screen is what produced the two
 * screens that imported this and never called it, and the nine that never imported it at all.
 *
 * Listeners are registered in the capture phase on `window` so a candidate who is typing, scrolling or
 * tapping anywhere on the screen keeps their session alive, and a candidate who walked away does not.
 * `onIdle` must be a stable reference — the store actions are.
 */
export function useIdleTimeout(
  onIdle: () => void,
  enabled = true,
  timeoutMs = CHECK_IN_IDLE_TIMEOUT_MS,
): void {
  useEffect(() => {
    // `enabled` rather than a conditional call, because a hook cannot be called conditionally — and rather
    // than a caller passing `Infinity`, which arms a timer that fires and holds a listener for nothing.
    if (!enabled) return;

    let timer = window.setTimeout(onIdle, timeoutMs);

    const restart = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(onIdle, timeoutMs);
    };

    const events = ['pointerdown', 'keydown'] as const;
    for (const event of events) {
      window.addEventListener(event, restart, true);
    }

    return () => {
      window.clearTimeout(timer);
      for (const event of events) {
        window.removeEventListener(event, restart, true);
      }
    };
  }, [onIdle, enabled, timeoutMs]);
}
